import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Youtube, Facebook, Twitter } from "lucide-react";
import { usePublicSettings } from "@/features/catalog/hooks";

const FOOTER_LINKS = {
  Kurumsal: [
    { to: "/hakkimizda", label: "Hakkımızda" },
    { to: "/iletisim", label: "İletişim" },
    { to: "/blog", label: "Blog" },
  ],
  Eğitim: [
    { to: "/jsps-sinavi", label: "JSPS Sınavı Rehberi" },
    { to: "/egitimler", label: "Tüm Eğitimler" },
    { to: "/paketler", label: "Paketler" },
  ],
  Yasal: [
    { to: "/kvkk-aydinlatma", label: "KVKK Aydınlatma" },
    { to: "/mesafeli-satis-sozlesmesi", label: "Mesafeli Satış Sözleşmesi" },
    { to: "/iptal-iade-kosullari", label: "İptal & İade" },
    { to: "/gizlilik-politikasi", label: "Gizlilik Politikası" },
    { to: "/cerez-politikasi", label: "Çerez Politikası" },
  ],
};

export default function Footer() {
  const { data: settings } = usePublicSettings();
  const logoUrl = settings?.site_logo_footer_url;
  const logoAlt = settings?.site_logo_alt || "Mersa Akademi";
  const email = settings?.site_email || "info@mersaakademi.com";
  const phone = settings?.site_phone || "+90 551 355 18 39";
  const phoneHref = `tel:${(phone || "").replace(/\s+/g, "")}`;
  const address =
    settings?.site_address ||
    "Mustafa Kemal Mah., 2082. Cad., No:2, Çankaya/Ankara";
  const instagram = settings?.social_instagram || "https://instagram.com/mersa_akedemi_";
  const youtube =
    settings?.social_youtube ||
    "https://www.youtube.com/channel/UCwsn9R54QOBRJ3Y3ipYFANQ";
  const facebook = settings?.social_facebook;
  const twitter = settings?.social_twitter;

  return (
    <footer className="border-t border-border bg-gradient-to-br from-brand-900 via-brand-800 to-pink-700 text-white/85">
      <div className="container-wide py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white text-brand-700 text-lg font-bold shadow-soft">
                <span aria-hidden>M</span>
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt={logoAlt}
                    width={44}
                    height={44}
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover p-1"
                  />
                )}
              </div>
              <div>
                <div className="font-bold text-white">{logoAlt}</div>
                <div className="text-xs uppercase tracking-wider text-white/70">JSPS Hazırlık</div>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/80">
              Jandarma personeline yönelik JSPS sınavına hazırlık platformu. Canlı dersler, video kayıtları, PDF kaynaklar ve soru bankaları.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-white">
                <Mail className="h-4 w-4" /> {email}
              </a>
              <a href={phoneHref} className="flex items-center gap-2 hover:text-white">
                <Phone className="h-4 w-4" /> {phone}
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{address}</span>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              {instagram && (
                <a
                  href={instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {youtube && (
                <a
                  href={youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  aria-label="YouTube"
                >
                  <Youtube className="h-4 w-4" />
                </a>
              )}
              {facebook && (
                <a
                  href={facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  aria-label="Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {twitter && (
                <a
                  href={twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  aria-label="Twitter"
                >
                  <Twitter className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {Object.entries(FOOTER_LINKS).map(([title, links]) => (
            <div key={title}>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">{title}</h3>
              <ul className="space-y-2 text-sm">
                {links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-white/75 hover:text-white transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-white/70 md:flex-row md:items-center md:justify-between">
          <p>&copy; {new Date().getFullYear()} {logoAlt}. Tüm hakları saklıdır.</p>
          <p>JSPS Sınavı'na hazırlık alanında öncü.</p>
        </div>
      </div>
    </footer>
  );
}
